if (!customElements.get('mayura-ingredient-slider')) {
  customElements.define(
    'mayura-ingredient-slider',
    class MayuraIngredientSlider extends HTMLElement {
      connectedCallback() {
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.tabletQuery = window.matchMedia('(min-width: 750px)');
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.updateStep = this.updateStep.bind(this);
        this.onDetailsClick = this.onDetailsClick.bind(this);
        this.onCardClick = this.onCardClick.bind(this);
        this.onCardPointerDown = this.onCardPointerDown.bind(this);
        this.onCardPointerMove = this.onCardPointerMove.bind(this);
        this.onDialogKeydown = this.onDialogKeydown.bind(this);
        this.onDialogPointerDown = this.onDialogPointerDown.bind(this);
        this.onDialogPointerUp = this.onDialogPointerUp.bind(this);
        this.desktopQuery.addEventListener('change', this.updateStep);
        this.tabletQuery.addEventListener('change', this.updateStep);
        this.mobileQuery.addEventListener('change', this.updateStep);

        this.cards = Array.from(this.querySelectorAll('.mayura-ingredient-card'));
        this.sliderTrack = this.querySelector('[id^="Slider-"]');
        this.updateCounter = this.updateCounter.bind(this);
        this.sliderTrack?.addEventListener('scroll', this.updateCounter, { passive: true });
        this.addEventListener('slideChanged', this.updateCounter);
        this.counterObserver = new ResizeObserver(this.updateCounter);
        if (this.sliderTrack) this.counterObserver.observe(this.sliderTrack);
        this.createDialog();

        this.cards.forEach((card) => {
          card.addEventListener('click', this.onCardClick);
          card.addEventListener('pointerdown', this.onCardPointerDown, { passive: true });
          card.addEventListener('pointermove', this.onCardPointerMove, { passive: true });
        });
        this.querySelectorAll('[data-mayura-ingredient-toggle]').forEach((button) => {
          button.addEventListener('click', this.onDetailsClick);
        });
        this.updateStep();
      }

      disconnectedCallback() {
        this.sliderTrack?.removeEventListener('scroll', this.updateCounter);
        this.removeEventListener('slideChanged', this.updateCounter);
        this.counterObserver?.disconnect();
        this.desktopQuery?.removeEventListener('change', this.updateStep);
        this.tabletQuery?.removeEventListener('change', this.updateStep);
        this.mobileQuery?.removeEventListener('change', this.updateStep);
        this.cards?.forEach((card) => {
          card.removeEventListener('click', this.onCardClick);
          card.removeEventListener('pointerdown', this.onCardPointerDown);
          card.removeEventListener('pointermove', this.onCardPointerMove);
        });
        this.querySelectorAll('[data-mayura-ingredient-toggle]').forEach((button) => {
          button.removeEventListener('click', this.onDetailsClick);
        });
        this.dialog?.remove();
        this.updateScrollLock();
      }

      updateStep() {
        const step = this.desktopQuery.matches
          ? this.dataset.desktopStep
          : this.tabletQuery.matches
            ? this.dataset.tabletStep
            : this.dataset.mobileStep;

        this.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
          button.dataset.step = step || '1';
        });

        this.cards?.forEach((card) => {
          card.classList.toggle('mayura-ingredient-card--mobile-trigger', this.mobileQuery.matches);
        });

        this.querySelectorAll('[data-mayura-ingredient-toggle]').forEach((button) => {
          if (!button.dataset.originalControls) button.dataset.originalControls = button.getAttribute('aria-controls') || '';
          button.setAttribute(
            'aria-controls',
            this.mobileQuery.matches ? this.dialog.id : button.dataset.originalControls
          );
        });

        if (!this.mobileQuery.matches && this.dialog?.open) this.closeDialog();
        this.updateCounter();
      }

      updateCounter() {
        requestAnimationFrame(() => {
          const current = this.querySelector('.slider-counter--current');
          const total = this.querySelector('.slider-counter--total');
          if (!current || !total || !this.sliderTrack || !this.cards.length) return;
          const offset = this.cards[1] ? this.cards[1].getBoundingClientRect().left - this.cards[0].getBoundingClientRect().left : 0;
          const index = offset > 0 ? Math.round(this.sliderTrack.scrollLeft / offset) : 0;
          const step = Math.max(1, Number(this.desktopQuery.matches ? this.dataset.desktopStep : this.tabletQuery.matches ? this.dataset.tabletStep : this.dataset.mobileStep) || 1);
          const pages = Math.ceil(this.cards.length / step);
          current.textContent = String(Math.min(pages, Math.ceil(index / step) + 1));
          total.textContent = String(pages);
          const visibleTotal = this.querySelector('.mayura-ingredient-slider__item-total');
          if (visibleTotal) visibleTotal.textContent = String(pages);
        });
      }

      onDetailsClick(event) {
        if (!this.mobileQuery.matches) return;
        event.preventDefault();
        event.stopPropagation();
        this.openCard(event.currentTarget.closest('.mayura-ingredient-card'));
      }

      onCardClick(event) {
        if (!this.mobileQuery.matches || this.cardPointerMoved) return;
        if (event.target.closest('a, button')) return;
        this.openCard(event.currentTarget);
      }

      onCardPointerDown(event) {
        this.cardPointerStart = { x: event.clientX, y: event.clientY };
        this.cardPointerMoved = false;
      }

      onCardPointerMove(event) {
        if (!this.cardPointerStart) return;
        const distanceX = Math.abs(event.clientX - this.cardPointerStart.x);
        const distanceY = Math.abs(event.clientY - this.cardPointerStart.y);
        if (distanceX > 8 || distanceY > 8) this.cardPointerMoved = true;
      }

      createDialog() {
        this.dialog = document.createElement('dialog');
        this.dialog.id = `MayuraIngredientOverlay-${this.closest('.shopify-section')?.id || Math.random().toString(36).slice(2)}`;
        this.dialog.className = 'mayura-ingredient-overlay';
        this.dialog.setAttribute('aria-label', this.dataset.dialogLabel || 'Inhaltsstoffdetails');

        const surface = document.createElement('div');
        surface.className = 'mayura-ingredient-overlay__surface';

        const toolbar = document.createElement('div');
        toolbar.className = 'mayura-ingredient-overlay__toolbar';

        this.counter = document.createElement('span');
        this.counter.className = 'mayura-ingredient-overlay__counter';
        this.counter.setAttribute('aria-live', 'polite');

        this.closeButton = document.createElement('button');
        this.closeButton.type = 'button';
        this.closeButton.className = 'mayura-ingredient-overlay__close';
        this.closeButton.setAttribute('aria-label', this.dataset.closeLabel || 'Schliessen');
        this.closeButton.innerHTML = '<span aria-hidden="true">&times;</span>';
        this.closeButton.addEventListener('click', () => this.closeDialog());

        toolbar.append(this.counter, this.closeButton);

        this.dialogContent = document.createElement('div');
        this.dialogContent.className = 'mayura-ingredient-overlay__content';

        const navigation = document.createElement('div');
        navigation.className = 'mayura-ingredient-overlay__navigation';

        this.previousButton = this.createNavigationButton('previous', '&lsaquo;');
        this.nextButton = this.createNavigationButton('next', '&rsaquo;');
        this.previousButton.addEventListener('click', () => this.showCard(this.activeIndex - 1, 'previous'));
        this.nextButton.addEventListener('click', () => this.showCard(this.activeIndex + 1, 'next'));
        navigation.append(this.previousButton, this.nextButton);

        surface.append(toolbar, this.dialogContent, navigation);
        this.dialog.append(surface);
        this.append(this.dialog);

        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeDialog();
        });
        this.dialog.addEventListener('keydown', this.onDialogKeydown);
        this.dialogContent.addEventListener('pointerdown', this.onDialogPointerDown, { passive: true });
        this.dialogContent.addEventListener('pointerup', this.onDialogPointerUp, { passive: true });
      }

      createNavigationButton(direction, symbol) {
        const sourceButton = this.querySelector(`button[name="${direction}"]`);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `mayura-ingredient-overlay__nav mayura-ingredient-overlay__nav--${direction}`;
        button.setAttribute('aria-label', sourceButton?.getAttribute('aria-label') || direction);
        button.innerHTML = `<span aria-hidden="true">${symbol}</span>`;
        return button;
      }

      openCard(card) {
        if (!card || !this.mobileQuery.matches) return;
        this.returnFocus = card.querySelector('[data-mayura-ingredient-toggle]') || card;
        const index = this.cards.indexOf(card);
        this.showCard(index < 0 ? 0 : index);
        document.documentElement.classList.add('mayura-ingredient-overlay-open');
        this.dialog.showModal();
        this.cards[this.activeIndex]
          ?.querySelector('[data-mayura-ingredient-toggle]')
          ?.setAttribute('aria-expanded', 'true');
        this.closeButton.focus();
      }

      showCard(index, direction = 'next') {
        if (!this.cards.length) return;
        this.activeIndex = (index + this.cards.length) % this.cards.length;

        const card = this.cards[this.activeIndex].cloneNode(true);
        card.removeAttribute('id');
        card.removeAttribute('role');
        card.removeAttribute('tabindex');
        card.removeAttribute('aria-haspopup');
        card.classList.remove('slider__slide', 'mayura-ingredient-card--mobile-trigger');
        card.classList.add('mayura-ingredient-card--overlay');
        const media = card.querySelector('.mayura-ingredient-card__media');
        if (media) {
          Array.from(media.classList).filter((name) => name.startsWith('mayura-content-media--')).forEach((name) => media.classList.remove(name));
        }
        card.querySelector('[data-mayura-ingredient-toggle]')?.remove();
        card.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        const details = card.querySelector('.mayura-ingredient-card__details');
        const description = details?.querySelector('.mayura-ingredient-card__description');
        const origin = card.querySelector('.mayura-ingredient-card__origin-mobile');
        const badges = card.querySelector('.mayura-ingredient-card__badges');
        if (details && origin) {
          if (description) description.after(origin);
          else details.prepend(origin);
        }
        if (details && badges) {
          const preceding = origin || description;
          if (preceding) preceding.after(badges);
          else details.prepend(badges);
        }
        if (this.dataset.studiesUrl && this.dataset.studiesLabel) {
          const link = document.createElement('a');
          link.className = 'mayura-ingredient-overlay__studies';
          link.href = this.dataset.studiesUrl;
          link.textContent = this.dataset.studiesLabel;
          const ingredientHeading = card.querySelector('.mayura-ingredient-card__title')?.textContent.trim() || '';
          const articleOverlay = this.closest('.shopify-section')?.querySelector('mayura-content-overlay[data-mode="article"]');
          if (ingredientHeading && typeof articleOverlay?.openAtHeading === 'function') {
            link.addEventListener('click', (event) => {
              event.preventDefault();
              const returnFocus = this.returnFocus;
              this.closeDialog(false);
              articleOverlay.openAtHeading(ingredientHeading, returnFocus);
            });
          }
          card.querySelector('.mayura-ingredient-card__content')?.append(link);
        }

        this.cards.forEach((item, itemIndex) => {
          item.querySelector('[data-mayura-ingredient-toggle]')?.setAttribute(
            'aria-expanded',
            itemIndex === this.activeIndex && this.dialog.open ? 'true' : 'false'
          );
        });

        this.counter.textContent = `${this.activeIndex + 1} / ${this.cards.length}`;
        this.dialogContent.replaceChildren(card);
        this.dialogContent.scrollTop = 0;
        this.previousButton.hidden = this.cards.length < 2;
        this.nextButton.hidden = this.cards.length < 2;

        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          card.animate(
            [
              { opacity: 0, transform: `translateX(${direction === 'previous' ? '-1.2rem' : '1.2rem'})` },
              { opacity: 1, transform: 'translateX(0)' },
            ],
            { duration: 220, easing: 'ease-out' }
          );
        }
      }

      closeDialog(restoreFocus = true) {
        if (!this.dialog?.open) return;
        this.dialog.close();
        this.updateScrollLock();
        this.cards.forEach((card) => {
          card.querySelector('[data-mayura-ingredient-toggle]')?.setAttribute('aria-expanded', 'false');
        });
        if (restoreFocus) this.returnFocus?.focus?.();
      }

      updateScrollLock() {
        document.documentElement.classList.toggle(
          'mayura-ingredient-overlay-open',
          Boolean(document.querySelector('dialog.mayura-ingredient-overlay[open]'))
        );
      }

      onDialogKeydown(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showCard(this.activeIndex + 1, 'next');
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showCard(this.activeIndex - 1, 'previous');
        }
      }

      onDialogPointerDown(event) {
        this.dialogPointerStart = { x: event.clientX, y: event.clientY };
      }

      onDialogPointerUp(event) {
        if (!this.dialogPointerStart) return;
        const distanceX = event.clientX - this.dialogPointerStart.x;
        const distanceY = event.clientY - this.dialogPointerStart.y;
        this.dialogPointerStart = null;
        if (Math.abs(distanceX) < 45 || Math.abs(distanceX) < Math.abs(distanceY) * 1.2) return;
        this.showCard(this.activeIndex + (distanceX < 0 ? 1 : -1), distanceX < 0 ? 'next' : 'previous');
      }
    }
  );
}
